import { Checkbox } from "@mantine/core";
import type {
  CellContext,
  Column,
  ColumnDef,
  Row,
} from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/table-core";
import get from "lodash/get";
import type { CSSProperties } from "react";

import {
  CompetencyCell,
  ReprocessCell,
  TextCell,
  type TextCellValue,
  UnviewedIndicatorTextCell,
} from "@/components/table/cells";
import ActionMenu from "@/components/table/cells/actionMenu";
import { CreatedAtCell } from "@/components/table/cells/created-at-cell";
import { getCommonPinningStyles } from "@/components/table/utils";
import {
  CompetencyBadge,
  NotAsked,
  QualifyingBadge,
} from "@/components/upwage-badge";
import {
  BaseTableQueryKeys,
  CreatedAtQueryKeys,
  SortOrder,
  type ApiColumn,
  type BaseTableQueryParams,
  type CreatedAtQueryParams,
  type FilterableCompetencyFits,
} from "@/types/api";
import {
  Competency,
  CompetencyFit,
  QUALIFYING_PASS_RATE_STRING_PATH,
  Screening,
} from "@/types/screeners";
import type { User } from "@/types/user";
import { numberIsTruthyOrZero } from "@/utils/helpers";
import { cellWithInfoProp } from "@/utils/table";
import { rowColorCodes } from "@/utils/ui";

export interface ResultsTableQueryParams
  extends BaseTableQueryParams,
    CreatedAtQueryParams {
  completedOnly: boolean;
  highlightColor: string[];
  highTimeSpent: boolean;
  highWordCount: boolean;
  language: string[];
  overallFit: FilterableCompetencyFits;
  unviewed: boolean;
}

export const ResultsQueryKeys = {
  ...BaseTableQueryKeys,
  ...CreatedAtQueryKeys,
  completedOnly: "completedOnly",
  highlightColor: "highlightColor",
  highTimeSpent: "highTimeSpent",
  highWordCount: "highWordCount",
  language: "language",
  overallFit: "overallFit",
  unviewed: "unviewed",
} as const satisfies { [K in keyof ResultsTableQueryParams]: K };

export const ResultsFilterKeys = [
  "completedOnly",
  "createdAtAfter",
  "createdAtBefore",
  "highlightColor",
  "highTimeSpent",
  "highWordCount",
  "language",
  "overallFit",
  "search",
  "unviewed",
] as const satisfies readonly (keyof ResultsTableQueryParams)[];

export enum ResultsTableSortableColumns {
  CandidateName = "candidateName",
  CreatedAt = "createdAt",
  LocationName = "locationName",
  OverallFit = "overallFit",
  QualifyingPassRate = "qualifyingPassRate",
  TotalTimeInSeconds = "totalTimeInSeconds",
  TotalWords = "totalWords",
}

export const RESULTS_DEFAULT_SORT = {
  sortBy: ResultsTableSortableColumns.CreatedAt,
  sortOrder: SortOrder.Desc,
} as const;

export const getJobPinningStyles = (
  column: Column<Screening>,
  row?: Row<Screening>,
): CSSProperties => ({
  ...getCommonPinningStyles(column, row),
  backgroundColor: row?.original.highlightColor
    ? rowColorCodes[row.original.highlightColor]
    : "white",
});

export const formatJobColumns = (
  columns: ApiColumn<Screening>[],
  user: User | null,
  // See explanation at
  // components/table/views/screeners/screeners-columns.ts:formatScreenerColumns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<Screening, any>[] => {
  const columnHelper = createColumnHelper<Screening>();

  const formattedColumns = columns.map((column) => {
    const { fieldName, internalName: header } = column;

    if (fieldName.startsWith("competencies")) {
      const competencyName = fieldName.slice(fieldName.indexOf(".") + 1);

      return columnHelper.accessor(
        (originalRow) => get(originalRow, ["competencies", competencyName]),
        {
          cell: cellWithInfoProp<Screening, Competency>(CompetencyCell, {}),
          header,
          id: competencyName,
        },
      );
    }

    if (fieldName.startsWith("showInSummary")) {
      const propertyName = fieldName.slice(fieldName.indexOf(".") + 1);

      return columnHelper.accessor(
        (originalRow) => get(originalRow, ["showInSummary", propertyName]),
        {
          cell: (info) => {
            if (info.getValue() === CompetencyFit.NotAsked) {
              return <NotAsked />;
            }

            return <TextCell info={info} />;
          },
          header,
          id: propertyName,
        },
      );
    }

    switch (fieldName) {
      case "candidateName":
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, string>(
            UnviewedIndicatorTextCell,
            {},
          ),
          header,
        });
      case "createdAt":
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, string>(CreatedAtCell, {}),
          header,
        });
      case QUALIFYING_PASS_RATE_STRING_PATH:
        return columnHelper.accessor(
          (row) => row.qualifyingSummary?.qualifyingPassRate ?? undefined,
          {
            cell: ({ row: { original } }) => {
              if (!original.qualifyingSummary) {
                return null;
              }
              const rawScore =
                numberIsTruthyOrZero(original.rawQualifyingQuestionsTotal) &&
                numberIsTruthyOrZero(original.rawQualifyingQuestionsQualified)
                  ? `${original.rawQualifyingQuestionsQualified}/${original.rawQualifyingQuestionsTotal}`
                  : undefined;

              return (
                <QualifyingBadge
                  rawScore={rawScore}
                  score={original.qualifyingSummary.qualifyingPassRate}
                />
              );
            },
            header,
            id: ResultsTableSortableColumns.QualifyingPassRate,
          },
        );
      case "overallFit":
        return columnHelper.accessor(fieldName, {
          cell: (info) => {
            const { overallFit, rawOverallFitScore } = info.row.original;

            if (!overallFit) {
              return null;
            }

            return (
              <CompetencyBadge
                isOverallFit
                rawScore={rawOverallFitScore}
                score={overallFit}
              />
            );
          },
          header,
        });
      case "words":
      case "time":
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, TextCellValue>(TextCell, {}),
          header,
          id:
            fieldName === "words"
              ? ResultsTableSortableColumns.TotalWords
              : ResultsTableSortableColumns.TotalTimeInSeconds,
        });
      default:
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<Screening, TextCellValue>(TextCell, {}),
          header,
        });
    }
  });

  const actionsColumn = {
    header: "Actions",
    accessorKey: "actions",
    cell: (info: CellContext<Screening, void>) => (
      <div
        className="flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <ActionMenu row={info.row.original} />
        {user?.isInternal && <ReprocessCell info={info} />}
      </div>
    ),
  };

  const highlightColorColumn = {
    header: "",
    accessorKey: "highlightColor",
    cell: () => null,
    isVisible: false,
  };

  const selectionColumn: ColumnDef<Screening> = {
    id: "selection",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        indeterminate={table.getIsSomePageRowsSelected()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(event) => {
          event.stopPropagation();
        }}
      />
    ),
  };

  return [
    selectionColumn,
    ...formattedColumns,
    actionsColumn,
    highlightColorColumn,
  ];
};
